import { useEffect, useState } from 'react';

import { getFetchBaseQueryErrorMessage } from 'helpers/api';
import { useToast } from 'hooks/toast';
import { useTranslation } from 'react-i18next';

import { EmailTemplateForm } from 'components/EmailTemplateForm/EmailTemplateForm';
import { EmailTemplatePreview } from 'components/EmailTemplatePreview/EmailTemplatePreview';
import { Button, InputGroup, Select } from 'components/FormComponents';

import {
  useCreateEmailTemplateMutation,
  useLazyGetEmailTemplateDefaultPreviewQuery,
  useLazyGetEmailTemplatesQuery,
} from 'api/email-templates-api';

const STANDARD_VALUE = '';

export const EmailTemplateStep = ({
  brand = {},
  category = 'candidate',
  type = null,
  submitLabel,
  footerLeft,
  standardName,
  standardDescription,
  previewStandardMessage,
  hideFooter = false,
  onSelectedTemplateIdChange,
  templateIdInBrandDefault,
  onBack,
  onSubmit = () => {},
  submitTestId,
}) => {
  const { t } = useTranslation();
  const showToast = useToast();

  const [selectedId, setSelectedId] = useState(STANDARD_VALUE);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);

  const previewType = Array.isArray(type) ? type[0] : type;
  const typeKey = Array.isArray(type) ? type.join(',') : type;

  const [fetchTemplates, { data: templates = [], isFetching: isLoadingTemplates }] =
    useLazyGetEmailTemplatesQuery({ category, type });
  const [createEmailTemplate, { isLoading: isCreating }] = useCreateEmailTemplateMutation();
  const [fetchDefaultPreview, { data: defaultPreview, isFetching: isFetchingDefault }] =
    useLazyGetEmailTemplateDefaultPreviewQuery();

  useEffect(() => {
    fetchTemplates({ category, type });
  }, [category, typeKey, fetchTemplates]);

  useEffect(() => {
    if (selectedId === STANDARD_VALUE && previewType) {
      fetchDefaultPreview({ type: previewType, brandId: brand?.id });
    }
  }, [selectedId, previewType, brand?.id]);

  useEffect(() => {
    if (!templateIdInBrandDefault || !templates.length) return;
    const id = Number(templateIdInBrandDefault);
    if (!Number.isNaN(id) && templates.some(t => t.id === id)) {
      setSelectedId(id);
    }
  }, [templateIdInBrandDefault, templates]);

  const selectedTemplate = templates.find(t => t.id === selectedId) ?? null;

  useEffect(() => {
    onSelectedTemplateIdChange?.(selectedTemplate?.id ?? null);
  }, [selectedTemplate, onSelectedTemplateIdChange]);

  const handleCreateTemplate = async data => {
    const result = await createEmailTemplate(data);
    if (result.error) {
      const detail = getFetchBaseQueryErrorMessage(result.error);
      showToast(t('common.error'), detail || t('emailTemplates.errors.createFailed'), 'error');
      return;
    }
    const newTemplate = result.data;
    setSelectedId(newTemplate.id);
    setIsCreatingTemplate(false);
    fetchTemplates({ category, type });
  };

  const handleSubmit = () => {
    onSubmit(selectedTemplate?.id ?? null);
  };

  const standardTemplate = {
    id: null,
    name: standardName ?? t('addCheck.emailTemplate.standardName'),
    description: standardDescription ?? t('addCheck.emailTemplate.standardDescription'),
  };

  if (isCreatingTemplate) {
    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <span className="text-2xl font-semibold text-neutral-7">
            {t('emailTemplates.form.createTitle')}
          </span>
          <Button type="link" onClick={() => setIsCreatingTemplate(false)}>
            {t('emailTemplates.backToSelect')}
          </Button>
        </div>
        <EmailTemplateForm
          onSubmit={handleCreateTemplate}
          onCancel={() => setIsCreatingTemplate(false)}
          isLoading={isCreating}
          initialValues={{ category }}
          showSupportFooterInPreview={brand?.include_support_footer ?? true}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="flex flex-col">
          <span className="text-2xl font-semibold text-neutral-7 mb-1">
            {t('addCheck.emailTemplate.title')}
          </span>
          <p className="text-xl text-neutral-6 mb-5">{t('addCheck.emailTemplate.subtitle')}</p>
        </div>
        <button
          type="button"
          className="text-xl font-semibold text-primary-4 bg-primary-1 hover:bg-primary-5 hover:text-neutral-1 px-3 py-1 rounded-full transition-colors"
          onClick={() => setIsCreatingTemplate(true)}>
          + {t('emailTemplates.createButton')}
        </button>
      </div>

      {isLoadingTemplates ? (
        <p className="text-xl text-neutral-5 py-4 text-center">{t('common.loading')}</p>
      ) : (
        <>
          <InputGroup title={t('addCheck.emailTemplate.selectLabel')}>
            <Select
              name="emailTemplateSelect"
              className="w-full"
              value={selectedId}
              onChange={e =>
                setSelectedId(e.target.value ? Number(e.target.value) : STANDARD_VALUE)
              }>
              {!templateIdInBrandDefault && (
                <Select.Item value={STANDARD_VALUE} description={standardTemplate.description}>
                  {standardTemplate.name}
                </Select.Item>
              )}
              {templates.map(template => (
                <Select.Item key={template.id} value={template.id} description={template.subject}>
                  {template.name}
                  {templateIdInBrandDefault === template.id ? ' (Brand Default)' : ''}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>

          <div>
            {selectedId === STANDARD_VALUE ? (
              isFetchingDefault ? (
                <p className="text-xl text-neutral-5 py-4 text-center">{t('common.loading')}</p>
              ) : defaultPreview ? (
                <EmailTemplatePreview
                  brand={brand}
                  subject={defaultPreview.subject}
                  body={defaultPreview.body}
                  showSupportFooter={false}
                  showActions
                />
              ) : null
            ) : selectedTemplate ? (
              <EmailTemplatePreview
                brand={brand}
                subject={selectedTemplate.subject}
                body={selectedTemplate.body}
                showSupportFooter={brand?.include_support_footer ?? true}
                showActions
              />
            ) : null}
          </div>
        </>
      )}

      {!hideFooter && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
          <div className="flex flex-wrap items-center gap-3">{footerLeft}</div>
          <div className="flex flex-wrap items-center gap-3 ml-auto">
            {onBack && (
              <Button type="secondary" onClick={onBack}>
                {t('common.back')}
              </Button>
            )}
            <Button loading={isCreating} onClick={handleSubmit} dataTestId={submitTestId}>
              {submitLabel ?? t('addCheck.sendButton')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
